import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useUnfollowUserMutation } from "Api/Mutations/Social/useUnfollowUserMutation";
import { useMutualFollowersQuery } from "Api/Queries/Social/useMutualFollowersQuery";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { SocialSummaryRow } from "Components/Social/SocialSummaryRow";
import { SocialUserActionsMenu } from "Components/Social/SocialUserActionsMenu";
import { SocialListPagination } from "Pages/People/SocialListPagination";
import {
  useSnapBackToLastPage,
  useSocialListPage,
} from "Pages/People/useSocialListPage";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.People;

/**
 * A page of the caller's mutual followers with an unfollow action per row — unfollowing ends the
 * mutual follow (their name is hidden again) but leaves any chat between them as it is.
 */
export const MutualListSection = () => {
  const { t } = useResource();
  const { currentPage, setPage } = useSocialListPage();
  const { data: mutuals, isLoading } = useMutualFollowersQuery(currentPage);
  useSnapBackToLastPage(mutuals, currentPage, setPage);
  const { mutate: unfollow, isPending } = useUnfollowUserMutation();

  return (
    <PrimaryPaper>
      <Stack gap={3}>
        {isLoading && (
          <Stack alignItems="center" py={1}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {mutuals?.totalCount === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t(RESOURCES.NoMutuals)}
          </Typography>
        )}
        {!!mutuals?.items.length && (
          <Stack gap={4}>
            {mutuals.items.map((mutual) => (
              <SocialSummaryRow
                key={mutual.identityUserPublicID}
                user={mutual}
                action={
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled={isPending}
                      onClick={() => unfollow(mutual.identityUserPublicID)}
                    >
                      {t(RESOURCES.Unfollow)}
                    </Button>
                    <SocialUserActionsMenu
                      identityUserPublicID={mutual.identityUserPublicID}
                      handle={mutual.handle}
                      displayName={mutual.displayName}
                      isBlockVisible
                      isDisabled={isPending}
                    />
                  </>
                }
              />
            ))}
          </Stack>
        )}
        {mutuals && (
          <SocialListPagination
            totalCount={mutuals.totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
          />
        )}
      </Stack>
    </PrimaryPaper>
  );
};
